<template>
  <div>
    <h5 class="text-center mb-2">Top 10 Countries</h5>
    <table class="table table-striped table-success table-sm table-bordered">
      <thead>
      <tr>
        <th>Country</th>
        <th>Visits</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, idx) in topCountries" :key="idx">
        <td>{{ item.country }}</td>
        <td>{{ item.visits }}</td>
      </tr>
      </tbody>
    </table>
    <div v-if="loading">Loading...</div>
    <div v-if="error" class="text-danger">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import countryMap from '@/utils/countryMapESEN.js';

const topCountries = ref([]);
const loading = ref(false);
const error = ref(null);

onMounted(/**
 * Asynchronous function to fetch and process user's visited link data, generating a sorted list of the top 10 countries by visit count.
 *
 * The function retrieves the user's id and authentication token from session storage and makes an API GET request to fetch the `linksVisited` data associated with the user. It processes the fetched data to map and count visited countries, translates country names based on a predefined mapping, sorts them in descending order of visit count, and selects the top 10 visited countries.
 *
 * If an error occurs during data retrieval or processing, an error message is set. The function toggles the loading state throughout the execution.
 *
 * Variables:
 * - `loading`: Reactive state representing the loading status of the operation.
 * - `topCountries`: Reactive array storing the top 10 visited countries with their visit counts.
 * - `error`: Reactive state to store error messages.
 * - `countryMap`: Object mapping country names from Spanish to English for translation.
 *
 * External dependencies:
 * - `axios`: Library for making HTTP requests.
 * - `sessionStorage`: Browser storage to retrieve user-specific data (id and token).
 * - `process.env.VUE_APP_API_URL`: Environment variable specifying the API base URL.
 */
async () => {
  loading.value = true;
  const apiUrl = process.env.VUE_APP_API_URL;
  const token = sessionStorage.getItem('token');
  try {
    const id = sessionStorage.getItem('id');
    const res = await axios.get(`${apiUrl}/linksvisited/id_user/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res && Array.isArray(res.data.linksVisited)) {
      const counts = {};
      res.data.linksVisited.forEach(item => {
        // Traduce el país si existe en el mapa, si no deja el original
        const countryES = item?.country || 'Unknown';
        const countryEN = countryMap[countryES] || countryES;
        counts[countryEN] = (counts[countryEN] || 0) + 1;
      });
      topCountries.value = Object.entries(counts)
          .map(([country, visits]) => ({ country, visits }))
          .sort((a, b) => b.visits - a.visits)
          .slice(0, 10);
    }
  } catch (e) {
    error.value = 'Failed to load country data';
  } finally {
    loading.value = false;
  }
});
</script>
